import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, Loader2, Mail, Phone, MapPin, Calendar, User,
  ShoppingBag, TrendingUp, DollarSign, Clock, AlertCircle,
  MapPinned, PartyPopper, Users as UsersIcon, ChevronDown, ChevronRight,
} from "lucide-react";
import { api } from "../../lib/api";
import { CustomerDetail, HistorialItem, PatronItem } from "../../features/customers/types";
import { RfmBadge } from "../../components/RfmBadge";
import { useInicio } from "../../lib/permisos";

function formatCurrency(n?: number | null): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("es-VE", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatDate(d?: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "2-digit" });
}

function formatDateShort(d?: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("es-ES", { year: "2-digit", month: "short", day: "2-digit" });
}

const CONTEXTO_LABELS: Record<string, string> = {
  mesa: "Mesa",
  terraza: "Terraza",
  barra: "Barra",
  llevar: "Para llevar",
  delivery: "Delivery",
};

const OCASION_LABELS: Record<string, string> = {
  comida: "Comida",
  cena: "Cena",
  desayuno: "Desayuno",
  brunch: "Brunch",
  "merienda/postre": "Merienda",
  celebracion: "Celebración",
  "reunion negocios": "Reunión negocios",
  "solo cafe": "Solo café",
};

export function CustomerDetailPage() {
  const inicio = useInicio();
  const { id } = useParams();
  const codCliente = parseInt(id ?? "0", 10);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["customer", codCliente],
    queryFn: () => api<CustomerDetail>(`/v1/customers/${codCliente}`),
    enabled: codCliente > 0,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="size-8 animate-spin text-brand-500" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-danger">
        <AlertCircle className="size-6 mr-2" />
        Error: {(error as Error)?.message ?? "Cliente no encontrado"}
      </div>
    );
  }

  const { basico, segment, metricas, historial, extended, fuente, topProductos, patron } = data;
  const totalNeto = historial.reduce((sum, h) => sum + (h.esDevolucion ? -h.totalNeto : h.totalNeto), 0);

  function toggleExpand(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className="min-h-screen bg-ink-50 pb-12">
      <div className="mx-auto max-w-7xl px-6 py-8 animate-fade-in">
        {/* Breadcrumb */}
        <div className="flex items-center gap-4 mb-4 text-sm">
          <Link to={inicio} className="inline-flex items-center gap-2 text-ink-500 hover:text-brand-600">
            <ArrowLeft className="size-4" />
            Panel
          </Link>
          <span className="text-ink-300">/</span>
          <Link to="/customers" className="text-ink-500 hover:text-brand-600">
            Clientes
          </Link>
        </div>

        {/* Cabecera */}
        <div className="card p-6 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-xs text-ink-400">#{basico.codCliente}</span>
                {fuente === "demo" && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-warning/10 text-warning font-medium">
                    DEMO
                  </span>
                )}
                {basico.bloqueado && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-danger/10 text-danger font-medium">
                    BLOQUEADO
                  </span>
                )}
              </div>
              <h1 className="font-display text-3xl font-bold text-ink-800 mb-1">{basico.nombre}</h1>
              {basico.alias && <p className="text-ink-500 mb-3">{basico.alias}</p>}
              <div className="flex flex-wrap items-center gap-4 text-sm text-ink-600">
                {basico.email && (
                  <span className="inline-flex items-center gap-1.5">
                    <Mail className="size-3.5" />
                    {basico.email}
                  </span>
                )}
                {(basico.mobil ?? basico.telefono1) && (
                  <span className="inline-flex items-center gap-1.5">
                    <Phone className="size-3.5" />
                    {basico.mobil ?? basico.telefono1}
                  </span>
                )}
                {basico.poblacion && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="size-3.5" />
                    {basico.poblacion}{basico.provincia ? `, ${basico.provincia}` : ""}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <RfmBadge label={segment?.segmentLabel} rfm={segment?.rfmScore} size="md" />
              {segment && (
                <p className="text-xs text-ink-500 mt-2">
                  RFM <span className="font-mono">{segment.rfmScore}</span>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card p-5">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="size-5 text-brand-500" />
              <span className="text-xs text-ink-500">LTV total</span>
            </div>
            <div className="font-display text-2xl font-bold text-ink-800">
              {formatCurrency(segment?.ltv ?? metricas.totalVentas)}
            </div>
          </div>
          <div className="card p-5">
            <div className="flex items-center justify-between mb-2">
              <ShoppingBag className="size-5 text-info" />
              <span className="text-xs text-ink-500">Transacciones</span>
            </div>
            <div className="font-display text-2xl font-bold text-ink-800">{metricas.numTransacciones}</div>
          </div>
          <div className="card p-5">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="size-5 text-success" />
              <span className="text-xs text-ink-500">Ticket promedio</span>
            </div>
            <div className="font-display text-2xl font-bold text-ink-800">{formatCurrency(metricas.ticketPromedio)}</div>
          </div>
          <div className="card p-5">
            <div className="flex items-center justify-between mb-2">
              <Clock className={`size-5 ${(metricas.diasInactivo ?? 0) > 180 ? "text-warning" : "text-ink-500"}`} />
              <span className="text-xs text-ink-500">Última compra</span>
            </div>
            <div className="font-display text-2xl font-bold text-ink-800">
              {metricas.diasInactivo != null ? `${metricas.diasInactivo}d` : "—"}
            </div>
            <p className="text-xs text-ink-500 mt-1">{formatDate(metricas.ultimaCompra)}</p>
          </div>
        </div>

        {/* Patron y Top productos */}
        {(patron || topProductos) && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {patron && patron.contexto.length > 0 && (
              <PatronCard
                title="Dónde se sienta"
                icon={<MapPinned className="size-4" />}
                rows={patron.contexto}
                labelMap={CONTEXTO_LABELS}
              />
            )}
            {patron && patron.ocasion.length > 0 && (
              <PatronCard
                title="En qué ocasión viene"
                icon={<PartyPopper className="size-4" />}
                rows={patron.ocasion}
                labelMap={OCASION_LABELS}
              />
            )}
            {topProductos && topProductos.length > 0 && (
              <div className="card p-5">
                <h3 className="font-display font-semibold text-ink-800 mb-3 flex items-center gap-2">
                  <ShoppingBag className="size-4" />
                  Productos favoritos
                </h3>
                <ul className="space-y-1.5 text-sm">
                  {topProductos.slice(0, 6).map((p) => (
                    <li key={p.codigo} className="flex items-center justify-between gap-2">
                      <span className="text-ink-700 truncate flex-1">{p.nombre}</span>
                      <span className="text-xs text-ink-500 tabular-nums whitespace-nowrap">
                        ×{p.numVeces}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Cuerpo en dos columnas */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna izquierda: datos personales + CRM */}
          <div className="lg:col-span-1 space-y-6">
            <div className="card p-5">
              <h3 className="font-display font-semibold text-ink-800 mb-4 flex items-center gap-2">
                <User className="size-4" />
                Datos personales
              </h3>
              <dl className="space-y-2 text-sm">
                <Item label="CIF/NIF" value={basico.cif} />
                <Item label="Fecha nacimiento" value={basico.fechaNacimiento ? formatDate(basico.fechaNacimiento) : undefined} />
                <Item label="Sexo" value={basico.sexo === "M" ? "Masculino" : basico.sexo === "F" ? "Femenino" : undefined} />
                <Item label="País" value={basico.codpais} />
                <Item label="Procedencia" value={basico.procedencia} />
                <Item label="Zona" value={basico.zona} />
                <Item label="Recibir info" value={basico.recibirInformacion ? "Sí" : "No"} />
              </dl>
            </div>

            {extended && (
              <div className="card p-5">
                <h3 className="font-display font-semibold text-ink-800 mb-4">CRM</h3>
                <dl className="space-y-2 text-sm">
                  {extended.alergias && extended.alergias.length > 0 && (
                    <Item label="⚠ Alergias" value={extended.alergias.join(", ")} />
                  )}
                  <Item label="Ocasión típica" value={extended.ocasionTipica} />
                  <Item label="Contexto" value={extended.contextoVisita} />
                  <Item label="Canal preferido" value={extended.canalPreferido} />
                  <Item label="Gusta descuentos" value={extended.gustaDescuentos ? "Sí" : undefined} />
                  {extended.notasPrivadas && (
                    <div className="pt-2">
                      <dt className="text-ink-500 text-xs mb-1">Notas privadas</dt>
                      <dd className="text-ink-700 text-sm bg-ink-50 p-3 rounded-md">{extended.notasPrivadas}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
          </div>

          {/* Historial */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="p-5 border-b border-ink-200">
                <h3 className="font-display font-semibold text-ink-800 flex items-center gap-2">
                  <Calendar className="size-4" />
                  Historial — últimas {historial.length} transacciones
                </h3>
              </div>
              {historial.length === 0 ? (
                <div className="p-12 text-center text-ink-400">Sin transacciones</div>
              ) : (
                <div className="divide-y divide-ink-100">
                  {historial.map((h, idx) => (
                    <HistorialRow
                      key={h.id ?? idx}
                      item={h}
                      expanded={expanded.has(String(h.id ?? idx))}
                      onToggle={() => toggleExpand(String(h.id ?? idx))}
                    />
                  ))}
                </div>
              )}
              {historial.length > 0 && (
                <div className="p-4 bg-ink-50 border-t border-ink-200 flex justify-between text-sm">
                  <span className="text-ink-600 font-medium">
                    Total mostrado ({historial.length} transacciones)
                  </span>
                  <span className="font-mono font-bold text-brand-700">{formatCurrency(totalNeto)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HistorialRow({
  item,
  expanded,
  onToggle,
}: {
  item: HistorialItem;
  expanded: boolean;
  onToggle: () => void;
}) {
  const hasLineas = item.lineas && item.lineas.length > 0;

  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={hasLineas ? onToggle : undefined}
          className={`flex items-center gap-2 text-left ${hasLineas ? "hover:text-brand-600" : "cursor-default"}`}
          aria-expanded={expanded}
        >
          {hasLineas ? (
            expanded ? <ChevronDown className="size-4 text-ink-400" /> : <ChevronRight className="size-4 text-ink-400" />
          ) : (
            <span className="size-4" />
          )}
          <div>
            <div className="text-sm text-ink-800 font-medium">
              {formatDateShort(item.fecha)}
              <span className="ml-2 font-mono text-xs text-ink-500">
                {item.numserie ?? "A"}-{item.numalbaran}
              </span>
              {item.esDevolucion && (
                <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-danger/10 text-danger">DEVOL</span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-1 text-xs text-ink-500">
              {item.contexto && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-ink-100">
                  <MapPinned className="size-3" />
                  {CONTEXTO_LABELS[item.contexto] ?? item.contexto}
                </span>
              )}
              {item.ocasion && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-ink-100">
                  <PartyPopper className="size-3" />
                  {OCASION_LABELS[item.ocasion] ?? item.ocasion}
                </span>
              )}
              {item.numPersonas && item.numPersonas > 1 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-ink-100">
                  <UsersIcon className="size-3" />
                  {item.numPersonas}
                </span>
              )}
            </div>
          </div>
        </button>
        <div className={`font-mono text-sm font-medium whitespace-nowrap ${item.esDevolucion ? "text-danger" : "text-ink-800"}`}>
          {item.esDevolucion ? "−" : ""}{formatCurrency(item.totalNeto)}
        </div>
      </div>

      {expanded && hasLineas && (
        <div className="mt-3 ml-6 bg-ink-50 rounded-md p-3">
          <table className="w-full text-xs">
            <thead className="text-ink-500">
              <tr>
                <th className="text-left font-medium pb-1">Artículo</th>
                <th className="text-right font-medium pb-1">Cant.</th>
                <th className="text-right font-medium pb-1">P.U.</th>
                <th className="text-right font-medium pb-1">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {item.lineas!.map((l, i) => (
                <tr key={i} className="border-t border-ink-200">
                  <td className="py-1 text-ink-700">
                    {l.nombre}
                    {l.categoria && <span className="ml-1 text-ink-400">· {l.categoria}</span>}
                  </td>
                  <td className="py-1 text-right tabular-nums">{l.cantidad}</td>
                  <td className="py-1 text-right tabular-nums">{formatCurrency(l.precioUnitario)}</td>
                  <td className="py-1 text-right tabular-nums font-medium">{formatCurrency(l.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PatronCard({
  title,
  icon,
  rows,
  labelMap,
}: {
  title: string;
  icon: React.ReactNode;
  rows: PatronItem[];
  labelMap: Record<string, string>;
}) {
  const total = rows.reduce((s, r) => s + r.num, 0) || 1;
  return (
    <div className="card p-5">
      <h3 className="font-display font-semibold text-ink-800 mb-3 flex items-center gap-2">
        {icon}
        {title}
      </h3>
      <div className="space-y-2">
        {rows.map((r) => {
          const pct = Math.round((r.num * 100) / total);
          return (
            <div key={r.valor}>
              <div className="flex justify-between text-xs mb-0.5">
                <span className="text-ink-700">{labelMap[r.valor] ?? r.valor}</span>
                <span className="text-ink-500 tabular-nums">{r.num} ({pct}%)</span>
              </div>
              <div className="h-1.5 bg-ink-100 rounded-full overflow-hidden">
                <div className="h-full bg-brand-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Item({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-ink-500 text-xs">{label}</dt>
      <dd className="text-ink-700 font-medium text-right">{value}</dd>
    </div>
  );
}